import React, { Fragment, useEffect, useState } from "react";
import SearchBar from "@components/inputs/SearchBar/SearchBar";
import InfoCard from "@components/display/InfoCard/InfoCard";
import PaginationControl from "@components/controls/PaginationControl/PaginationControl";
import Button from "@components/buttons/Button/Button";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import commonStyles from "@styles/CommonPageLayout.module.css";
import { fetchBusinesses, updateBusiness } from "@services/businessService";
import { Business } from "@models/Business";
import { filterByNameOrMessage } from "@util/filter";
import ActivateConfirmationPopup from "@components/modals/ActivateConfirmationPopup/ActivateConfirmationPopup";
import DeactivateConfirmationPopup from "@components/modals/DeactivateConfirmationPopup/DeactivateConfirmationPopup";
import { SetAdminStatus, Status } from "@models/Common";
import { useNavigate } from "react-router-dom";
import LoadSaveErrorDisplay from "@components/display/LoadSaveErrorDisplay/LoadSaveErrorDisplay";
import { getRootDomain, regionBadge } from "@util/domain";

const RESULTS_PER_PAGE = 8;

const adminStatusOrder: Record<SetAdminStatus, number> = {
    set: 0,
    pending: 1,
    "not set": 2,
};

const BusinessesPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeactivatePopup, setShowDeactivatePopup] = useState(false);
    const [showActivatePopup, setShowActivatePopup] = useState(false);
    const [selectedBusinessId, setSelectedBusinessId] = useState("");

    // Fetch businesses on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await fetchBusinesses();
                setBusinesses(data);
            } catch (err) {
                setError("Failed to fetch businesses.");
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, []);

    // Filter and paginate businesses
    const filteredData = filterByNameOrMessage(businesses, searchTerm);

    const sortedData = [...filteredData].sort((a, b) => {
    // Sort by setAdminStatus first
        const statusDiff = adminStatusOrder[a.setAdminStatus] - adminStatusOrder[b.setAdminStatus];
        if (statusDiff !== 0) return statusDiff;

        // Then alphabetically by name
        return a.name.localeCompare(b.name);
    });

    const totalPages = Math.ceil(sortedData.length / RESULTS_PER_PAGE);
    const paginatedData = sortedData.slice(
        (currentPage - 1) * RESULTS_PER_PAGE,
        currentPage * RESULTS_PER_PAGE,
    );

    const handleSetBusinessStatus = async (id: string, status: Status) => {
        try {
            setLoading(true);
            await updateBusiness(id, { status });
            // Update business status locally
            setBusinesses((prevState) => {
                const newState = [...prevState];
                const index = prevState.findIndex((val) => val.id === id);
                if (index > -1) {
                    newState[index].status = status;
                }
                return newState;
            });
        } catch {
            setError("Failed to set business status.");
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async () => {
        setShowActivatePopup(false);
        await handleSetBusinessStatus(selectedBusinessId, "active");
    };

    const handleDeactivate = async () => {
        setShowDeactivatePopup(false);
        await handleSetBusinessStatus(selectedBusinessId, "deactivated");
    };

    const renderContent = () => {
        if (loading || error) {
            return <LoadSaveErrorDisplay isLoading={loading} error={error} />;
        }

        if (paginatedData.length === 0) {
            return <div className={`${commonStyles.noContent} ${commonStyles.note}`}>{`No businesses ${searchTerm ? "found" : "yet"}.`}</div>;
        }

        return (
            <>
                <div className={commonStyles.itemList}>
                    {paginatedData.map((data) => (
                        <InfoCard
                            key={data.id}
                            title={data.name}
                            badge={regionBadge(data.region)}
                            media={{
                                type: "image",
                                src: data.logo,
                            }}
                            items={[
                                // `Subscription: ${data.subscription}`,
                                `Memorial Credits: ${data.memorialCreditAllocation + data.bonusMemorialCredits}`,
                                `Subdomain: ${data.subdomain}`,
                                ...(data.contactName
                                    ? [
                                        `Contact Name: ${data.contactName}`,
                                    ]
                                    : []),
                                ...(data.iframeUrl
                                    ? [
                                        `iFrame: ${data.iframeUrl}`,
                                    ]
                                    : []),
                                ...(data.firehawkNoticesUrl
                                    ? [
                                        `Firehawk Notices: ${data.firehawkNoticesUrl}`,
                                    ]
                                    : []),
                                ...(data.naocaNoticesUrl
                                    ? [
                                        `Naoca Notices: ${data.naocaNoticesUrl}`,
                                    ]
                                    : []),
                            ]}
                            buttons={[
                                {
                                    title: (
                                        <>
                                            Open
                                            <OpenInNewIcon
                                                style={{ marginLeft: "3px" }}
                                                fontSize="inherit"
                                            />
                                        </>
                                    ),
                                    variant: "text",
                                    href: `${getRootDomain(data.subdomain || "memorials")}`,
                                    newTab: true,
                                },
                                {
                                    title: "Guest Book",
                                    variant: "primary" as const,
                                    onClick: () => navigate(`/memorials/guest-book?${new URLSearchParams({ exampleBusinessId: data.id }).toString()}`),
                                },
                                {
                                    title: "Service Sheet",
                                    variant: "primary" as const,
                                    onClick: () => navigate(`/memorials/service-sheet?${new URLSearchParams({ exampleBusinessId: data.id }).toString()}`),
                                },
                                ...(data.status === "active"
                                    ? [
                                        // {
                                        //     title: "Add Subscription",
                                        //     variant: "primary" as const,
                                        //     onClick: () => {},
                                        // },
                                        // {
                                        //     title: "Add Credits",
                                        //     variant: "primary" as const,
                                        //     onClick: () => {},
                                        // },
                                    ]
                                    : []),
                                ...(data.setAdminStatus !== "set"
                                    ? [
                                        {
                                            title: data.setAdminStatus === "not set" ? "Set Admin" : "Change Admin",
                                            variant: data.setAdminStatus === "not set" ? "submit" as const : "secondary" as const,
                                            onClick: () => navigate(`set-admin?${new URLSearchParams({
                                                id: data.id,
                                                mode: data.setAdminStatus === "not set" ? "set" : "change",
                                            }).toString()}`),
                                        },
                                    ]
                                    : []),
                                {
                                    title: "Edit",
                                    variant: "secondary",
                                    to: `editor?${new URLSearchParams({ mode: "edit", id: data.id }).toString()}`,
                                },
                                {
                                    title: data.status === "active" ? "Deactivate" : "Activate",
                                    variant: data.status === "active" ? "cancel" : "primary",
                                    onClick: () => {
                                        setSelectedBusinessId(data.id);
                                        if (data.status === "active") {
                                            setShowDeactivatePopup(true);
                                        } else {
                                            setShowActivatePopup(true);
                                        }
                                    },
                                },
                            ]}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <div className={commonStyles.container}>
            <div className={commonStyles.headerContainer}>
                <h1>Businesses</h1>
                {!loading && !error && (
                    <div className={commonStyles.buttonGroup}>
                        <Button variant="primary" size="medium" to="editor?mode=add">
                            Add
                        </Button>
                    </div>
                )}
            </div>
            {!loading && (
                <div className={commonStyles.searchBar}>
                    <SearchBar value={searchTerm} onChange={(val) => setSearchTerm(val)} />
                </div>
            )}
            {renderContent()}
            {totalPages > 1 && (
                <div className={commonStyles.pagination}>
                    <PaginationControl
                        totalPages={totalPages}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                </div>
            )}
            <ActivateConfirmationPopup
                visible={showActivatePopup}
                onConfirm={handleActivate}
                onCancel={() => setShowActivatePopup(false)}
            />
            <DeactivateConfirmationPopup
                visible={showDeactivatePopup}
                onConfirm={handleDeactivate}
                onCancel={() => setShowDeactivatePopup(false)}
            />
        </div>
    );
};

export default BusinessesPage;
